import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'

console.info('[NOIZA][DEPLOY CHECK] payment-gateway-v1 ACTIVE')

export interface BillingInfo {
  restaurant_id: number
  /** Comercial / brand name shown at the top of the receipt (e.g. "NatuCafé Coffee Shop"). */
  business_name: string
  /**
   * Optional legal owner name for personas físicas under Régimen Simplificado
   * (e.g. "DIGNA DORA DURAN CAMACHO"). When present, it's printed in small
   * caps right below the business_name on the receipt. When null/empty the
   * receipt looks exactly as before — backwards compatible for Cabaña /
   * Rancho / Capricho / etc. Requires the column to exist in
   * `restaurant_billing_info`; run ADD_BILLING_LEGAL_NAME.sql once to add it.
   */
  legal_name?: string | null
  legal_id: string
  legal_id_type: string
  address: string | null
  phone: string | null
  email: string | null
  regime: string
  footer_note: string | null
  current_consecutive: number
}

export interface PaymentItemSnapshot {
  order_item_id: string
  item_name: string
  quantity: number
  unit_price: number
  line_total: number
  notes?: string | null
}

export interface ProcessPaymentInput {
  restaurantId: number | string
  orderId: string
  tableId?: string | null
  amount: number
  method: 'cash' | 'sinpe' | 'card'
  items: PaymentItemSnapshot[]
  isPartial: boolean
  createdBy?: string | null
}

export interface PaymentRecord {
  id: string
  restaurant_id: number
  order_id: string
  amount: number
  method: string
  items_json: PaymentItemSnapshot[]
  consecutive_number: number
  is_partial: boolean
  created_at: string
}

// ─── Hook: load billing info for a restaurant ─────────────────────────────
export function useBillingInfo(restaurantId: number | string | undefined) {
  const [info, setInfo] = useState<BillingInfo | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchInfo = useCallback(async () => {
    if (!restaurantId) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('restaurant_billing_info')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .maybeSingle()
      if (error) throw error
      setInfo((data as BillingInfo) || null)
    } catch (err) {
      console.error('[NOIZA] Error loading billing info:', err)
      setInfo(null)
    } finally {
      setLoading(false)
    }
  }, [restaurantId])

  useEffect(() => {
    fetchInfo()
  }, [fetchInfo])

  return { info, loading, refetch: fetchInfo }
}

// ─── Process a payment (full or partial) ──────────────────────────────────
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export async function processPayment(
  input: ProcessPaymentInput,
): Promise<{ success: boolean; payment?: PaymentRecord; error?: string }> {
  try {
    // 1) Reserve consecutive number atomically
    const { data: consecutiveData, error: consecErr } = await supabase.rpc(
      'increment_billing_consecutive',
      { p_restaurant_id: Number(input.restaurantId) },
    )
    if (consecErr) throw consecErr
    const consecutive = consecutiveData as number

    // Sanitize createdBy — must be a valid UUID or null
    const safeCreatedBy =
      input.createdBy && UUID_RE.test(input.createdBy) ? input.createdBy : null

    // ─── BLINDAJE: monto cobrado === monto facturado ───────────────────
    // Punto único de verdad. El monto que se cobra (payments.amount) DEBE
    // ser exactamente la suma de las líneas que se facturan/imprimen
    // (items_json[*].line_total) — incluyendo la línea sintética de
    // descuento, que ya viene con line_total negativo.
    //
    // Antes la UI pasaba `amount` y `items` por separado; si un bug de
    // cálculo hacía divergir uno del otro, la tarjeta cobraba un monto
    // distinto al impreso en el ticket/factura (ej: ₡8.800 cobrado vs
    // ₡9.800 facturado). Acá sellamos el invariante para TODOS los
    // restaurantes y TODOS los callers: derivamos el monto de las líneas.
    //
    // Sólo aplica cuando hay líneas detalladas. Los callers que pagan sin
    // detalle de ítems (items vacío) conservan el `amount` recibido.
    const itemsSum = (input.items || []).reduce(
      (s, i) => s + (Number(i.line_total) || 0),
      0,
    )
    const passedAmount = Number(input.amount) || 0
    let chargedAmount = passedAmount
    if ((input.items || []).length > 0) {
      // CRC no usa decimales en la práctica; tolerancia de 0.5 para
      // absorber cualquier ruido de redondeo flotante.
      if (Math.abs(itemsSum - passedAmount) > 0.5) {
        console.warn(
          '[NOIZA][billing] amount/items mismatch — sellando al total de líneas',
          {
            order_id: input.orderId,
            restaurant_id: input.restaurantId,
            passed_amount: passedAmount,
            items_sum: itemsSum,
            diff: passedAmount - itemsSum,
          },
        )
      }
      chargedAmount = Math.round(itemsSum * 100) / 100
    }

    // 2) Insert payment row
    const { data: payment, error: payErr } = await supabase
      .from('payments')
      .insert({
        restaurant_id: Number(input.restaurantId),
        order_id: input.orderId,
        table_id: input.tableId || null,
        amount: chargedAmount,
        method: input.method,
        items_json: input.items,
        item_count: input.items.reduce((s, i) => s + i.quantity, 0),
        consecutive_number: consecutive,
        is_partial: input.isPartial,
        created_by: safeCreatedBy,
      })
      .select('*')
      .single()
    if (payErr) throw payErr

    // 3) Mark order_items as paid — pero SOLO cuando la cantidad
    //    acumulada cobrada (incluyendo este pago) iguala/supera la
    //    quantity original de la línea. Esto permite cobros parciales
    //    por unidad (ej: 2 de 3 capuccinos) sin cerrar la línea —
    //    el saldo restante queda disponible para el siguiente cobro.
    // Excluimos el id sintético '__discount__' (y cualquier id vacío): NO es
    // una fila real de order_items, así que pasarlo a `.in('id', ...)` rompe
    // el cast de tipo en Postgres. El descuento ya se refleja en
    // chargedAmount y en items_json; acá solo necesitamos los ítems reales.
    const itemIds = Array.from(
      new Set(input.items.map((i) => i.order_item_id)),
    ).filter((id) => id && id !== '__discount__')
    if (itemIds.length > 0) {
      // Cargá las quantities originales de TODAS las líneas de la orden,
      // no sólo de las que vienen en este pago.
      //
      // BLINDAJE (NatuCafé Barra 1, orden 3719): antes se consultaba
      // `.in('id', itemIds)`. Si una línea se agregaba a la cuenta
      // después de que la caja abriera el modal (típico de Estantería /
      // retail), su fila podía no resolverse en esa consulta acotada y
      // quedaba SIN `paid_at` aunque su monto SÍ se hubiera cobrado en
      // `items_json`. Con una sola línea sin marcar, la orden nunca
      // pasaba a 'delivered' y la mesa quedaba ocupada para siempre.
      //
      // Leyendo la orden completa, cualquier línea cuya cantidad ya esté
      // cubierta por los pagos acumulados se cierra, sin importar en qué
      // momento se agregó. Las líneas realmente pendientes siguen
      // intactas: la condición de cierre no cambió.
      const { data: origItems, error: origErr } = await supabase
        .from('order_items')
        .select('id, quantity, paid_at')
        .eq('order_id', input.orderId)
      if (origErr) throw origErr

      // Cargá TODOS los pagos previos de esta orden (este pago ya
      // está insertado en step 2, así que se incluye) y sumá las
      // quantities por order_item_id.
      const { data: allPayments } = await supabase
        .from('payments')
        .select('items_json')
        .eq('order_id', input.orderId)
      // Las claves se normalizan a string: `items_json` guarda el id como
      // número y la fila lo devuelve como número o texto según el driver.
      // Comparar tipos distintos hacía fallar el match en silencio.
      const paidQtyByItem = new Map<string, number>()
      for (const p of allPayments || []) {
        const arr = (p as any).items_json || []
        for (const it of arr) {
          if (!it?.order_item_id) continue
          const key = String(it.order_item_id)
          const prev = paidQtyByItem.get(key) || 0
          paidQtyByItem.set(key, prev + (Number(it.quantity) || 0))
        }
      }

      // Cerrá solo las líneas que llegaron a su quantity original.
      // Se exige `paidQty > 0` para no cerrar por accidente una línea que
      // nunca se cobró, y se saltan las que ya tienen `paid_at` para no
      // reasignarles el comprobante de un pago posterior.
      const fullyPaidIds: string[] = []
      for (const orig of origItems || []) {
        if ((orig as any).paid_at) continue
        const origQty = Number(orig.quantity) || 0
        const paidQty = paidQtyByItem.get(String(orig.id)) || 0
        if (origQty > 0 && paidQty >= origQty) {
          fullyPaidIds.push(orig.id)
        }
      }

      if (fullyPaidIds.length > 0) {
        const { error: updErr } = await supabase
          .from('order_items')
          .update({
            paid_at: new Date().toISOString(),
            paid_in_payment_id: payment.id,
          })
          .in('id', fullyPaidIds)
        if (updErr) throw updErr
      }
    }

    // 4) If full payment (not partial) → close the order
    if (!input.isPartial) {
      await supabase
        .from('orders')
        .update({
          status: 'delivered',
          payment_method: input.method,
          updated_at: new Date().toISOString(),
        })
        .eq('id', input.orderId)
    } else {
      // Check if all items are now paid → close order anyway
      const { data: remaining } = await supabase
        .from('order_items')
        .select('id')
        .eq('order_id', input.orderId)
        .is('paid_at', null)
      if (!remaining || remaining.length === 0) {
        await supabase
          .from('orders')
          .update({
            status: 'delivered',
            payment_method: input.method,
            updated_at: new Date().toISOString(),
          })
          .eq('id', input.orderId)
      }
    }

    // 5) Cash drawer auto-open (Rancho San Francisco id=28 only).
    //    Lógica extraída a `enqueueRanchoCashDrawerJob` para que el
    //    flujo atómico (processTablePaymentAtomic) abra la gaveta con
    //    EXACTAMENTE las mismas condiciones. La apertura del hardware
    //    sigue viviendo en la app, nunca en la base.
    await enqueueRanchoCashDrawerJob({
      restaurantId: input.restaurantId,
      orderId: input.orderId,
      tableId: input.tableId ?? null,
      method: input.method,
      isPartial: input.isPartial,
      chargedAmount,
      payment: payment as PaymentRecord,
    })

    return { success: true, payment: payment as PaymentRecord }
  } catch (err: any) {
    console.error('[NOIZA] processPayment error:', err)
    return { success: false, error: err?.message || 'Error al procesar pago' }
  }
}

// ─── Gaveta de dinero (Rancho San Francisco, id 28) ───────────────────────
//
//    Encolamos un cash_drawer_job pending para que el bridge local
//    abra el cajón con el comando ESC/POS (\x1b\x70\x00\x19\xfa).
//    Esto SOLO se dispara desde el flujo "Cobrar / Facturar", así que
//    las comandas de cocina, los pedidos marketplace y los QR
//    self-service NO abren el cajón.
//
//    Scopeado a restaurant_id = 28 para no afectar a Cabaña ni a
//    ningún otro restaurante. Si la inserción falla, se loguea pero NO
//    rompe el flujo de cobro — el cobro ya quedó persistido.
async function enqueueRanchoCashDrawerJob(args: {
  restaurantId: number | string
  orderId: string
  tableId?: string | null
  method: string
  isPartial: boolean
  chargedAmount: number
  payment: PaymentRecord | null
}): Promise<void> {
  const { restaurantId, orderId, tableId, method, isPartial, chargedAmount } =
    args
  const payment = args.payment
  const restaurantIdNum = Number(restaurantId)
  if (restaurantIdNum === 28) {
      // ── Resolve the UUID for cash_drawer_jobs.order_id ────────────
      //
      // En este código base `orders.id` es un BIGINT numérico (ej "1958")
      // y el UUID real de la orden vive en `orders.tracking_token`.
      // Los callers de Cobrar/Facturar (PayTableModal, InvoiceRequestsPage)
      // pasan `orderId = table.current_order.id` o `order.id` — o sea
      // el BIGINT como string. Si lo metemos directo en
      // `cash_drawer_jobs.order_id` (UUID) Postgres revienta con
      // 22P02 invalid input syntax for type uuid: "1958".
      //
      // Solución: leemos `tracking_token` (UUID) y `order_number` de
      // la fila `orders` y usamos `tracking_token` como el order_id
      // del job. El BIGINT original se conserva en
      // `payload.order_internal_id` para auditoría.
      let resolvedOrderUuid: string | null = null
      let resolvedOrderNumber: string | null = null

      console.log('[NOIZA][cash-drawer] resolving order uuid', {
        order_id_in: orderId,
        order_id_typeof: typeof orderId,
      })

      try {
        const { data: orderRow, error: orderLookupErr } = await supabase
          .from('orders')
          .select('id, tracking_token, order_number')
          .eq('id', orderId)
          .maybeSingle()

        if (orderLookupErr) {
          console.warn(
            '[NOIZA][cash-drawer] order lookup error (non-blocking)',
            orderLookupErr,
          )
        }

        resolvedOrderUuid = (orderRow as any)?.tracking_token ?? null
        resolvedOrderNumber = (orderRow as any)?.order_number ?? null

        console.log('[NOIZA][cash-drawer] order uuid resolved', {
          order_id_in: orderId,
          orders_row_id: (orderRow as any)?.id ?? null,
          tracking_token: resolvedOrderUuid,
          order_number: resolvedOrderNumber,
        })
      } catch (orderLookupEx) {
        console.warn(
          '[NOIZA][cash-drawer] order lookup exception (non-blocking)',
          orderLookupEx,
        )
      }

      const drawerPayload = {
        trigger: 'cobrar_facturar',
        payment_id: (payment as any)?.id ?? null,
        consecutive_number: (payment as any)?.consecutive_number ?? null,
        amount: chargedAmount,
        method,
        is_partial: isPartial,
        table_id: tableId || null,
        // UUID real de la orden (tracking_token) — coincide con
        // cash_drawer_jobs.order_id.
        order_id: resolvedOrderUuid,
        // BIGINT interno de orders.id — sólo para auditoría /
        // debugging desde el bridge.
        order_internal_id: orderId,
        order_number: resolvedOrderNumber,
        created_at: (payment as any)?.created_at ?? null,
      }

      console.log('[NOIZA][cash-drawer] enqueue start', {
        restaurant_id_raw: restaurantId,
        restaurant_id_normalized: restaurantIdNum,
        order_id_uuid: resolvedOrderUuid,
        order_id_internal: orderId,
        order_number: resolvedOrderNumber,
        payment_id: (payment as any)?.id ?? null,
        amount: chargedAmount,
        method,
        is_partial: isPartial,
      })

      try {
        const { data: drawerRow, error: drawerErr } = await supabase
          .from('cash_drawer_jobs')
          .insert({
            restaurant_id: 28,
            // Sólo metemos UUID válido o NULL en la columna UUID. Si
            // por alguna razón el lookup falló, el job queda con
            // order_id NULL pero igual encola — el cajón debe abrir
            // aunque la trazabilidad de la orden se pierda.
            order_id: resolvedOrderUuid,
            order_number: resolvedOrderNumber,
            status: 'pending',
            payload: drawerPayload,
          })
          .select('id, status, created_at')
          .single()

        if (drawerErr) {
          console.warn('[NOIZA][cash-drawer] enqueue failed', {
            error_message: drawerErr.message,
            error_code: (drawerErr as any).code,
            error_details: (drawerErr as any).details,
            error_hint: (drawerErr as any).hint,
            error_full: drawerErr,
          })
        } else {
          console.log('[NOIZA][cash-drawer] enqueue success', {
            id: (drawerRow as any)?.id,
            status: (drawerRow as any)?.status,
            created_at: (drawerRow as any)?.created_at,
            order_id_uuid: resolvedOrderUuid,
            order_number: resolvedOrderNumber,
          })
        }
      } catch (drawerEx: any) {
        console.warn('[NOIZA][cash-drawer] enqueue exception', {
          message: drawerEx?.message,
          stack: drawerEx?.stack,
          full: drawerEx,
        })
      }
  } else {
    console.log('[NOIZA][cash-drawer] skipped — restaurant_id != 28', {
      restaurant_id_raw: restaurantId,
      restaurant_id_normalized: restaurantIdNum,
    })
  }
}

// ─── Cobro atómico de mesas (dine_in) ─────────────────────────────────────
//
// Reemplaza los 4 pasos de `processPayment` por UNA sola llamada a la RPC
// `cashier_register_payment_atomic_v2`, que corre dentro de una transacción
// en la base: o queda todo (consecutivo, pago, líneas marcadas, orden
// cerrada) o no queda nada. Elimina el caso que dejó Barra 1 colgada.
//
// SOLO para el cobro real de mesas desde PayTableModal. El flujo retroactivo
// de InvoiceRequestsPage sigue usando `processPayment` sin cambios.
export interface ProcessTablePaymentAtomicInput {
  restaurantId: number | string
  /** `orders.id`. Se envía tal cual a la RPC, sin convertir. */
  orderId: string | number
  tableId?: string | null
  method: 'cash' | 'sinpe' | 'card'
  /**
   * Monto que la caja está mostrando y el cajero confirma, YA con el
   * descuento aplicado. La RPC lo valida contra el saldo real: si la
   * cuenta cambió mientras el modal estaba abierto, responde 40001.
   */
  expectedAmount: number
  /** true = cobrar todo lo pendiente. false = cobro parcial. */
  payAllUnpaid: boolean
  /**
   * Snapshot de las líneas que se están cobrando, tal cual lo arma la
   * caja. Incluye la línea sintética `__discount__` cuando hay descuento
   * y lleva la `quantity` parcial de cada línea, así que los cobros
   * parciales por unidad viajan completos a la RPC.
   */
  items: PaymentItemSnapshot[]
  notes?: string | null
  /**
   * UUID generado UNA vez al confirmar el cobro y reusado en cada
   * reintento. Es la llave de idempotencia: si el pago ya se registró,
   * la RPC devuelve el mismo registro en vez de duplicarlo.
   */
  paymentId: string
}

export interface ProcessTablePaymentAtomicResult {
  success: boolean
  payment?: PaymentRecord
  error?: string
  /**
   * true cuando la RPC respondió 40001: la cuenta cambió mientras el
   * modal estaba abierto. Hay que refrescar y pedir confirmar el saldo.
   */
  conflict?: boolean
}

/** 40001 = serialization_failure: la cuenta cambió bajo los pies del cajero. */
function isAccountChangedError(err: any): boolean {
  const code = String(err?.code ?? '')
  if (code === '40001') return true
  const msg = String(err?.message ?? '')
  return msg.includes('40001')
}

export async function processTablePaymentAtomic(
  input: ProcessTablePaymentAtomicInput,
): Promise<ProcessTablePaymentAtomicResult> {
  try {
    // Cobro vía la Edge Function `charge-table-payment`, que internamente
    // llama a la RPC transaccional única `gateway_charge` (advisory lock
    // por restaurant_id+order_id durante TODA la ejecución del cobro).
    // Reemplaza la llamada directa a `cashier_register_payment_atomic_v2`
    // — esa RPC quedó con EXECUTE revocado para clientes tras el
    // incidente y ya no es invocable desde acá.
    //
    // `supabase.functions.invoke` NO reintenta automáticamente: cada
    // click del cajero dispara exactamente una solicitud HTTP. El mismo
    // `input.paymentId` (generado una vez por intento de cobro y
    // reutilizado si el cajero reintenta manualmente) viaja como
    // `payment_id` — es la llave de idempotencia que `gateway_charge`
    // usa para devolver el pago ya existente en vez de duplicarlo.
    const { data, error } = await supabase.functions.invoke(
      'charge-table-payment',
      {
        body: {
          order_id: input.orderId,
          method: input.method,
          pay_all_unpaid: input.payAllUnpaid !== false,
          // El descuento viaja dentro de `items` como la línea sintética
          // `__discount__`, y cada línea lleva su `quantity` parcial. La
          // Edge Function arma `items_json` y valida el monto contra el
          // saldo real.
          items: input.items,
          expected_amount: input.expectedAmount,
          notes: input.notes ?? null,
          payment_id: input.paymentId,
        },
      },
    )

    // `error` cubre TANTO fallas de transporte (sin red, DNS, Edge
    // Function caída) COMO respuestas HTTP no-2xx de la propia Edge
    // Function — supabase-js las envuelve como FunctionsHttpError sin
    // lanzar excepción, así que hay que revisar `error` explícitamente
    // en vez de confiar solo en el try/catch de abajo. El cuerpo real
    // `{data,error:{code,message,...}}` que arma el servidor solo es
    // legible vía `error.context.json()` cuando hay `context` — si no
    // lo hay, es una falla de transporte pura (no llegó a la función).
    if (error) {
      let code = 'INTERNAL'
      let message = error.message || 'Error al procesar el pago'
      try {
        const ctx: any = (error as any).context
        if (ctx && typeof ctx.json === 'function') {
          const body = await ctx.json()
          code = body?.error?.code || code
          message = body?.error?.message || message
        } else if (ctx?.error) {
          code = ctx.error.code || code
          message = ctx.error.message || message
        }
      } catch {
        /* sin cuerpo legible — falla de transporte pura */
      }

      if (code === '40001') {
        console.warn('[NOIZA][billing] cuenta cambió durante el cobro', {
          order_id: input.orderId,
          payment_id: input.paymentId,
        })
        return {
          success: false,
          conflict: true,
          error:
            'La cuenta cambió mientras cobrabas. Revisá el detalle y confirmá el saldo de nuevo.',
        }
      }
      if (code === 'SCOPE_BUSY') {
        return {
          success: false,
          error:
            'Ya hay un cobro en proceso para esta mesa. Esperá un momento — no hagas doble clic — y volvé a intentar.',
        }
      }
      if (code === 'RATE_LIMITED') {
        return {
          success: false,
          error:
            'Demasiados intentos en poco tiempo. Esperá unos segundos y volvé a intentar.',
        }
      }
      if (code === 'CIRCUIT_OPEN') {
        return {
          success: false,
          error:
            'Hubo varios errores seguidos cobrando esta mesa. Esperá un momento antes de reintentar.',
        }
      }

      console.warn('[NOIZA][billing] charge-table-payment error', {
        order_id: input.orderId,
        payment_id: input.paymentId,
        code,
        message,
      })
      return { success: false, error: message }
    }

    // Respuesta 2xx: `data` es el envelope {data, error} que arma
    // charge-table-payment. Acá `data.error` siempre es null — los
    // errores de negocio ya se resolvieron arriba, por status HTTP
    // no-2xx, no dentro de un 200.
    const payment = (data?.data ?? null) as PaymentRecord | null
    if (!payment?.id) {
      return {
        success: false,
        error: 'El cobro no devolvió un comprobante válido.',
      }
    }

    // Gaveta de Rancho: se abre en la app y SOLO después de que la Edge
    // Function confirmó éxito, con exactamente las mismas condiciones
    // de antes.
    await enqueueRanchoCashDrawerJob({
      restaurantId: input.restaurantId,
      orderId: String(input.orderId),
      tableId: input.tableId ?? null,
      method: input.method,
      isPartial: Boolean(payment.is_partial),
      chargedAmount: Number(payment.amount) || 0,
      payment,
    })

    return { success: true, payment }
  } catch (err: any) {
    // Excepción genuina (no cubierta por el `error` de arriba) — p.ej.
    // el propio `enqueueRanchoCashDrawerJob` o un fallo al parsear.
    console.error('[NOIZA] processTablePaymentAtomic error:', err)
    if (isAccountChangedError(err)) {
      return {
        success: false,
        conflict: true,
        error:
          'La cuenta cambió mientras cobrabas. Revisá el detalle y confirmá el saldo de nuevo.',
      }
    }
    return { success: false, error: err?.message || 'Error al procesar pago' }
  }
}

// ─── Render printable invoice HTML and open in new window ─────────────────
export function openPrintableInvoice(args: {
  info: BillingInfo
  payment: PaymentRecord
  tableLabel?: string | null
}) {
  const { info, payment, tableLabel } = args
  const date = new Date(payment.created_at).toLocaleString('es-CR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
  const consecStr = String(payment.consecutive_number).padStart(6, '0')
  const methodLabel =
    payment.method === 'cash'
      ? 'Efectivo'
      : payment.method === 'sinpe'
        ? 'SINPE Móvil'
        : 'Tarjeta'

  const fmt = (n: number) =>
    new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0,
    }).format(n)

  const itemsHtml = (payment.items_json || [])
    .map(
      (it) => `
      <tr>
        <td class="qty">${it.quantity}×</td>
        <td class="name">${escapeHtml(it.item_name)}${it.notes ? `<div class="note">${escapeHtml(it.notes)}</div>` : ''}</td>
        <td class="price">${fmt(it.line_total)}</td>
      </tr>`,
    )
    .join('')

  const html = `<!doctype html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>Factura #${consecStr} — ${escapeHtml(info.business_name)}</title>
  <style>
    @media print { @page { margin: 8mm; size: 80mm auto; } body { margin: 0; } .no-print { display:none !important; } }
    * { box-sizing: border-box; }
    body { font-family: 'Courier New', ui-monospace, monospace; color:#111; background:#f3f3f3; padding:24px; }
    .ticket { max-width: 320px; margin: 0 auto; background:#fff; padding:18px 16px; border:1px solid #e5e5e5; box-shadow: 0 4px 16px rgba(0,0,0,.06); }
    h1 { font-size: 16px; margin: 0 0 4px; text-align:center; letter-spacing: 1px; }
    .biz { text-align:center; font-size: 11px; line-height: 1.4; margin-bottom: 10px; }
    .biz strong { display:block; font-size:12px; }
    .meta { font-size: 11px; border-top: 1px dashed #999; border-bottom: 1px dashed #999; padding: 8px 0; margin: 8px 0; }
    .meta div { display:flex; justify-content: space-between; }
    table { width: 100%; border-collapse: collapse; margin: 6px 0; font-size: 11px; }
    td { padding: 3px 0; vertical-align: top; }
    td.qty { width: 28px; font-weight: 700; }
    td.name { padding-right: 6px; }
    td.price { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
    .note { font-size: 10px; color:#666; font-style: italic; }
    .totals { border-top: 1px dashed #999; padding-top: 8px; margin-top: 8px; font-size: 12px; }
    .totals .row { display:flex; justify-content:space-between; padding: 2px 0; }
    .totals .grand { font-size: 14px; font-weight: 800; border-top: 2px solid #111; margin-top: 4px; padding-top: 6px; }
    .pay { margin-top: 10px; padding: 6px 0; border-top: 1px dashed #999; font-size: 11px; display:flex; justify-content:space-between; }
    .footer { text-align:center; margin-top: 12px; font-size: 11px; color:#444; }
    .legal { text-align:center; margin-top: 8px; font-size: 9px; color:#666; line-height: 1.4; }
    .actions { max-width: 320px; margin: 12px auto 0; display:flex; gap: 8px; }
    .actions button { flex:1; padding: 10px; border: none; border-radius: 8px; font-weight:700; cursor:pointer; font-family: inherit; }
    .actions .print { background:#111; color:#fff; }
    .actions .close { background:#e5e5e5; color:#111; }
    .partial-badge { display:inline-block; background:#fef3c7; color:#92400e; font-size: 10px; padding: 2px 6px; border-radius: 4px; font-weight: 700; margin-left: 4px; }
  </style>
</head>
<body>
  <div class="ticket">
    <h1>${escapeHtml(info.business_name)}</h1>
    <div class="biz">
      <strong>Cédula ${escapeHtml(info.legal_id_type === 'fisica' ? 'Física' : 'Jurídica')}: ${escapeHtml(info.legal_id)}</strong>
      ${info.address ? `${escapeHtml(info.address)}<br/>` : ''}
      ${info.phone ? `Tel: ${escapeHtml(info.phone)}` : ''}
      ${info.email ? ` · ${escapeHtml(info.email)}` : ''}
    </div>

    <div class="meta">
      <div><span>Factura N°</span><strong>${consecStr}${payment.is_partial ? '<span class="partial-badge">PARCIAL</span>' : ''}</strong></div>
      <div><span>Fecha</span><span>${date}</span></div>
      ${tableLabel ? `<div><span>Mesa</span><span>${escapeHtml(tableLabel)}</span></div>` : ''}
    </div>

    <table>
      <thead>
        <tr style="border-bottom:1px solid #111; font-size:10px; text-transform:uppercase;">
          <td>Cant</td><td>Descripción</td><td style="text-align:right;">Total</td>
        </tr>
      </thead>
      <tbody>${itemsHtml}</tbody>
    </table>

    <div class="totals">
      <div class="row grand"><span>TOTAL</span><span>${fmt(payment.amount)}</span></div>
    </div>

    <div class="pay">
      <span>Forma de pago</span>
      <strong>${methodLabel}</strong>
    </div>

    ${info.footer_note ? `<div class="footer">${escapeHtml(info.footer_note)}</div>` : ''}
    <div class="legal">
      Régimen Tributario Simplificado<br/>
      Documento equivalente — no constituye factura electrónica
    </div>
  </div>

  <div class="actions no-print">
    <button class="close" onclick="window.close()">Cerrar</button>
    <button class="print" onclick="window.print()">Imprimir</button>
  </div>

  <script>setTimeout(() => window.print(), 350);</script>
</body>
</html>`

  const w = window.open('', '_blank', 'width=420,height=720')
  if (w) {
    w.document.write(html)
    w.document.close()
  }
}

function escapeHtml(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
